import {
  DataEntityPresenterModel,
  DataEntryPresenterModel,
  DataTypeEnum,
  FieldPresenterModel,
  TEMP_FIELD_COMPONENT_NAMES,
} from '@osos/shared/types';
import cloneDeep from 'lodash-es/cloneDeep';
import {
  CastHelper,
  CastingHelperFactory,
  InvalidTypeError,
  LocalizedJsonCastHelper,
} from '../../helpers/casting.helper';
import {
  hasFieldADTSource,
  isFieldRepeatable,
} from '../entity-fields/entity-field-utils';

enum ValueExpectedTypes {
  Single = 0,
  Array,
  DoubleArray,
}

export function normalizePresentedEntry(
  entry: DataEntryPresenterModel,
  entity: DataEntityPresenterModel,
): DataEntryPresenterModel {
  const _entryNormalizer = new EntryNormalizer(entry, entity);
  return _entryNormalizer.normalizeEntry();
}

class EntryNormalizer {
  constructor(
    private entry: DataEntryPresenterModel,
    private entity: DataEntityPresenterModel,
  ) {}

  private conversionMap = new Map<string, (value) => any>([
    [
      this.getConversionMapKey(
        ValueExpectedTypes.Single,
        ValueExpectedTypes.Array,
      ),
      (value: any): any[] =>
        value !== null && value !== undefined ? [value] : [],
    ],
    [
      this.getConversionMapKey(
        ValueExpectedTypes.Single,
        ValueExpectedTypes.DoubleArray,
      ),
      (value: any): Array<any[]> => [[value]],
    ],
    // tslint:disable-next-line: max-line-length
    [
      this.getConversionMapKey(
        ValueExpectedTypes.Array,
        ValueExpectedTypes.Single,
      ),
      (value: any[]): any => (value.length ? value[0] : null),
    ],
    [
      this.getConversionMapKey(
        ValueExpectedTypes.Array,
        ValueExpectedTypes.DoubleArray,
      ),
      (value: any[]): Array<any[]> => [value],
    ],
    // tslint:disable-next-line: max-line-length
    [
      this.getConversionMapKey(
        ValueExpectedTypes.DoubleArray,
        ValueExpectedTypes.Single,
      ),
      (value: Array<any[]>): any =>
        !!value.length && !!value[0].length ? value[0][0] : null,
    ],
    // tslint:disable-next-line: max-line-length
    [
      this.getConversionMapKey(
        ValueExpectedTypes.DoubleArray,
        ValueExpectedTypes.Array,
      ),
      (value: Array<any[]>): any[] => [].concat.apply([], value),
    ],
  ]);

  private defaultValuesMap = new Map<ValueExpectedTypes, any>([
    [ValueExpectedTypes.Single, null],
    [ValueExpectedTypes.Array, []],
    [ValueExpectedTypes.DoubleArray, []],
  ]);

  normalizeEntry(): DataEntryPresenterModel {
    if (!this.entry || !!this.entry?._obsoleteValue) {
      return this.entry;
    }

    this._prepareEntryNormalization();

    return this.entry;
  }

  private _prepareEntryNormalization(): void {
    if (
      this.entry?.isEntryObsolete ||
      this.entry.entityVersion !== this.entity.version
    ) {
      this.entry = cloneDeep(this.entry);
      this.entry._obsoleteValue = cloneDeep(this.entry?.value);
      this._castFieldsValues();
    }
  }

  _castFieldsValues(
    fields: FieldPresenterModel[] = this.entity.fields,
    value: any = this.entry.value,
  ): any {
    if (value === null || value === undefined) {
      return;
    }

    fields.forEach((_field) => {
      const fieldExpectedType = this.getFieldExpectedType(_field);
      const valueType = this.getValueType(value[_field.normalizedName]);

      if (fieldExpectedType !== valueType) {
        const converter: (value) => any = this.conversionMap.get(
          this.getConversionMapKey(valueType, fieldExpectedType),
        );
        value[_field.normalizedName] = converter
          ? converter(value[_field.normalizedName])
          : this.defaultValuesMap.get(valueType);
      }

      if (_field.dataType === DataTypeEnum.Block) {
        const innerFields = _field.block.internalEntity?.fields ?? [];
        if (!!innerFields.length && isFieldRepeatable(_field)) {
          value[_field.normalizedName].forEach((_repeatableBlockValues) => {
            this._castFieldsValues(innerFields, _repeatableBlockValues);
          });
        } else {
          this._castFieldsValues(innerFields, value[_field.normalizedName]);
        }
      } else {
        value[_field.normalizedName] = this._castFieldValue(
          _field,
          value[_field.normalizedName],
        );
      }
    });
  }

  private _castFieldValue(field: FieldPresenterModel, value: any): any {
    const valueType = this.getValueType(value);
    try {
      const _castedExpression = this.getCaster(field);
      let castedValue: any;
      // Two level array

      switch (valueType) {
        case ValueExpectedTypes.DoubleArray:
          if (!!value.length && !!value[0].length) {
            castedValue = value.map((vItem) =>
              vItem.map((v) => _castedExpression.cast(v)),
            );
          } else {
            castedValue = [];
          }
          break;
        case ValueExpectedTypes.Array:
          castedValue = value.map((v) => _castedExpression.cast(v));
          break;
        default:
          castedValue = _castedExpression.cast(value);
          break;
      }
      return castedValue;
    } catch (e) {
      if (e instanceof InvalidTypeError) {
        if (
          valueType === ValueExpectedTypes.Array ||
          valueType === ValueExpectedTypes.DoubleArray
        ) {
          return [];
        }
        return null;
      }
    }
    return null;
  }

  private _hasFieldMultiValue(field: FieldPresenterModel): boolean {
    return (
      !!field.options?.multiValue ||
      this._hasFieldRange(field) ||
      this.isSwitchField(field)
    );
  }

  /**
   * This either must be handled on component level or we need to make the options.isRange besides multi value
   */
  private _hasFieldRange(field: FieldPresenterModel): boolean {
    return field?.renderOptions?.editComponent?.options?.isRanged;
  }

  private isSwitchField(field: FieldPresenterModel): boolean {
    return (
      field.renderOptions.editComponent.component === 'SwitchComponent' &&
      field?.renderOptions?.editComponent?.options?.switchType !== 2
    );
  }

  private _isCategoryField(field: FieldPresenterModel): boolean {
    return (
      field.renderOptions.editComponent.component ===
      TEMP_FIELD_COMPONENT_NAMES.categoryComponent
    );
  }

  private isADTCollectionField(field: FieldPresenterModel): boolean {
    return (
      field.dataType === DataTypeEnum.Entity && this.isADTSource(field.source) // one source
    );
  }

  private getFieldExpectedType(field): ValueExpectedTypes {
    if (isFieldRepeatable(field) && this._hasFieldMultiValue(field)) {
      return ValueExpectedTypes.DoubleArray;
    }
    if (this._hasFieldMultiValue(field) || isFieldRepeatable(field)) {
      return ValueExpectedTypes.Array;
    }
    return ValueExpectedTypes.Single;
  }

  private getValueType(value): ValueExpectedTypes {
    if (Array.isArray(value) && !!value.length && Array.isArray(value[0])) {
      return ValueExpectedTypes.DoubleArray;
    }

    if (Array.isArray(value)) {
      return ValueExpectedTypes.Array;
    }

    return ValueExpectedTypes.Single;
  }

  private getConversionMapKey(
    a: ValueExpectedTypes,
    b: ValueExpectedTypes,
  ): string {
    return a + '_' + b;
  }

  private getCaster(field: FieldPresenterModel): CastHelper {
    if (
      this._isCategoryField(field) ||
      this.isSwitchField(field) ||
      this.isADTCollectionField(field)
    ) {
      return new LocalizedJsonCastHelper();
    }

    return CastingHelperFactory.getCastHelper(field.dataType);
  }

  private isADTSource(source: string): boolean {
    return hasFieldADTSource(source);
  }
}
